import { getInletMetadata } from 'aem-libs-react-ts/src/inlet/getInletMetadata'
import LanguageDetector from 'aem-libs-react-ts/src/services/LanguageDetector'
import { ApiConfig } from 'aem-libs-react-ts/src/types/app-config'

import { HttpHeader } from '../constants/httpHeader'
import { WebApiErrorResponse } from '../types'


const pathHeader = 'x-path'
const inletVersionHeader = 'x-inlet-version'
const inletNameHeader = 'x-inlet-name'
const contentTypeValue = 'application/json'

const { version, name } = getInletMetadata()

const defaultParseFunction = async (response: Response) => {
  const textResponse = await response.text()
  return textResponse ? JSON.parse(textResponse) : {}
}

export interface HttpClientResponse<T> {
  data: T
  isError: false
  response: Response
  request: RequestInitExtended
}

export interface HttpClientErrorResponse<E = unknown> {
  data: E
  isError: true
  response: Response
  request: RequestInitExtended
}

type ParseFn<T> = (response: Response) => Promise<T>

export const isWebApiErrorResponse = (response: unknown): response is HttpClientErrorResponse<WebApiErrorResponse> => {
  return isHttpClientErrorResponse(response) && Boolean(response.data as WebApiErrorResponse)
}

export const isHttpClientErrorResponse = <T>(response: unknown): response is HttpClientErrorResponse<T> => {
  return (response as HttpClientErrorResponse<T>).isError
}

const successResponse = async <T>(
  response: Response,
  request: RequestInitExtended,
  parseFn: ParseFn<T>
): Promise<HttpClientResponse<T>> => {
  const data = await parseFn(response)
  return { response, data, isError: false, request }
}

const errorResponse = async <E>(
  response: Response,
  request: RequestInitExtended,
  parseFn: ParseFn<E>
): Promise<HttpClientErrorResponse<E>> => {
  try {
    const data = await parseFn(response)
    return { response, isError: true, data, request }
  } catch {
    return { response, isError: true, data: {} as E, request }
  }
}

const inletHeaders: HeadersInit = {
  [pathHeader]: window.location.href,
  [inletVersionHeader]: version,
  [inletNameHeader]: name
}

type HttpOperationType = <T>(
  path: string,
  requestInit?: RequestInitExtended,
  options?: RequestOptions<T>
) => Promise<HttpClientResponse<T>>
type ApiClientResult = Record<'get' | 'post' | 'put' | 'delete', HttpOperationType>

interface RequestInitExtended extends RequestInit {
  payload?: any
}

interface IHttpClient extends ApiClientResult {}

interface RequestOptions<T> {
  parseFn?: ParseFn<T>
  controller?: AbortController
  keepDefaultContentType?: boolean
}

export class HttpClient implements IHttpClient {
  private readonly url: URL
  private readonly baseHeaders: HeadersInit

  constructor(config: ApiConfig, baseURL: string, options?: { customApiKeyHeader?: string }) {
    this.url = new URL(config.url + baseURL)
    this.baseHeaders = {
      ...inletHeaders,
      [options?.customApiKeyHeader ?? HttpHeader.WEB_API_KEY]: config.key,
      [HttpHeader.ACCEPT_LANGUAGE]: LanguageDetector()
    }
  }

  private makeHeaders = (requestHeaders: Record<string, string> | undefined) => {
    return new Headers({
      ...this.baseHeaders,
      ...(requestHeaders ? { ...requestHeaders } : {})
    })
  }

  private async request<T>(
    path: string,
    init: RequestInitExtended = {},
    options?: RequestOptions<T>
  ): Promise<HttpClientResponse<T>> {
    const { payload, ...requestInit } = init
    const customHeaders = (init.headers ?? {}) as Record<string, string>
    const requestContentType = customHeaders[HttpHeader.CONTENT_TYPE] ?? contentTypeValue

    const headers = this.makeHeaders(
      !options?.keepDefaultContentType
        ? {
            ...customHeaders,
            [HttpHeader.CONTENT_TYPE]: requestContentType
          }
        : customHeaders
    )

    const requestConfig: RequestInitExtended = {
      ...requestInit,
      ...(payload ? { body: JSON.stringify(payload) } : {}),
      headers,
      signal: options?.controller ? options.controller.signal : undefined
    }
    const url = `${this.url}${path}`

    return fetch(url, requestConfig).then(async (res) => {
      const parseFn = options?.parseFn ?? defaultParseFunction
      if (res.ok) {
        return successResponse(res, requestConfig, parseFn)
      }

      const errorResult = await errorResponse(res, requestConfig, parseFn)
      return Promise.reject(errorResult)
    })
  }

  get<T>(path: string, requestInit: RequestInitExtended = {}, options?: RequestOptions<T>) {
    return this.request<T>(path, { ...requestInit, method: 'GET' }, options)
  }

  post<T>(path: string, requestInit: RequestInitExtended = {}, options?: RequestOptions<T>) {
    return this.request<T>(path, { ...requestInit, method: 'POST' }, options)
  }

  put<T>(path: string, requestInit: RequestInitExtended = {}, options?: RequestOptions<T>) {
    return this.request<T>(path, { ...requestInit, method: 'PUT' }, options)
  }

  delete<T>(path: string, requestInit: RequestInitExtended = {}, options?: RequestOptions<T>) {
    return this.request<T>(path, { ...requestInit, method: 'DELETE' }, options)
  }
}
